import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import { 
  Microscope, 
  Sparkles, 
  Cpu, 
  Zap, 
  Shield, 
  Award,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  Factory,
  Layers,
  BarChart3
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Message Sent!",
        description: response.data.message || "Thank you for contacting us. We'll get back to you soon.",
      });
      
      // Reset form
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again or contact us directly at info@nanorbital.com",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-slate-200 z-50 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Logo />
            
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="text-slate-700 hover:text-blue-900 transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('products')} className="text-slate-700 hover:text-blue-900 transition-colors font-medium">Products</button>
              <button onClick={() => scrollToSection('applications')} className="text-slate-700 hover:text-blue-900 transition-colors font-medium">Applications</button>
              <button onClick={() => scrollToSection('industries')} className="text-slate-700 hover:text-blue-900 transition-colors font-medium">Industries</button>
              <Button onClick={() => scrollToSection('contact')} className="bg-blue-900 hover:bg-blue-800">
                Contact Us
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-slate-50 to-white opacity-70"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight">
                Advanced Silver
                <span className="block text-blue-900">Nanomaterials</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Industry-leading manufacturer of high-purity silver nanowires and nanoparticles, engineered for superior performance in next-generation electronics and advanced coating applications.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button onClick={() => scrollToSection('products')} size="lg" className="bg-blue-900 hover:bg-blue-800 text-lg px-8">
                  Explore Products
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <Button onClick={() => scrollToSection('contact')} size="lg" variant="outline" className="text-lg px-8 border-slate-300">
                  Get in Touch
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div>
                  <p className="text-3xl font-bold text-blue-900">500g/day</p>
                  <p className="text-sm text-slate-600">Production Capacity</p>
                </div>
                <div className="w-px h-12 bg-slate-300"></div>
                <div>
                  <p className="text-3xl font-bold text-blue-900">Trusted Quality</p>
                  <p className="text-sm text-slate-600">Proven Performance</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-transparent rounded-3xl transform rotate-3"></div>
              <img 
                src="https://images.unsplash.com/photo-1766297248160-87aca6fa59ef" 
                alt="Nanotechnology Laboratory" 
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover relative z-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">About Nanorbital</h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Based in Ahmedabad, Gujarat, Nanorbital Advanced Materials LLP delivers cutting-edge nanomaterial solutions backed by proprietary manufacturing technology and decades of materials science expertise.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Microscope className="w-8 h-8 text-blue-900" />
                </div>
                <CardTitle className="text-2xl">Research-Driven</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  Advanced continuous production technology enabling consistent, scalable manufacturing of high-purity nanomaterials with precision quality control.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Factory className="w-8 h-8 text-blue-900" />
                </div>
                <CardTitle className="text-2xl">Scale & Quality</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  Industrial-scale production capacity delivering up to 500g/day of high-aspect-ratio nanowires with exceptional conductivity and uniform specifications.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <BarChart3 className="w-8 h-8 text-blue-900" />
                </div>
                <CardTitle className="text-2xl">Reliable Supply</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  Consistent product availability with dependable supply chain management, ensuring your projects stay on schedule.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Products</h2>
            <p className="text-xl text-slate-600">
              High-performance nanomaterials engineered for precision and scalability
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors shadow-lg">
              <CardHeader>
                <img 
                  src="https://images.unsplash.com/photo-1738778151587-032287ae9f90" 
                  alt="Silver Nanowires" 
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
                <CardTitle className="text-3xl mb-2">Silver Nanowires</CardTitle>
                <CardDescription className="text-lg text-blue-900 font-semibold">N-Ag01 | N-Ag03</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Diameter:</span> 50-200 nm</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Length:</span> 10-100 μm</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Production:</span> Up to 500 g/day</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Features:</span> High-aspect-ratio, excellent conductivity</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Availability:</span> Custom formulations in various carrier solvents</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors shadow-lg">
              <CardHeader>
                <img 
                  src="https://images.unsplash.com/photo-1631376178637-392efc9e356b" 
                  alt="Silver Nanoparticles" 
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
                <CardTitle className="text-3xl mb-2">Silver Nanoparticles</CardTitle>
                <CardDescription className="text-lg text-blue-900 font-semibold">Advanced Formulations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Application:</span> Conductive inks and coatings</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Quality:</span> High purity and consistency</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Scalability:</span> Bulk production capabilities</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Customization:</span> Tailored specifications available</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-900 rounded-full mt-2"></div>
                    <p className="text-slate-700"><span className="font-semibold">Versatility:</span> Multiple industry applications</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section id="applications" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Applications</h2>
            <p className="text-xl text-slate-600">
              Enabling breakthrough innovations across diverse technological frontiers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                  <Layers className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">Conducting Inks & Coatings</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  High-performance conductive inks for printed electronics, enabling flexible and efficient circuit manufacturing.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                  <Cpu className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">Display Technologies</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Transparent conducting layers for advanced displays, touchscreens, and next-generation visual interfaces.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">Flexible Electronics</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Enabling bendable, wearable, and rollable electronic devices with superior conductivity on flexible substrates.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">Wearable Electrodes</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Advanced materials for health monitoring devices, smart textiles, and biomedical sensing applications.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                  <Microscope className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">Printing Technologies</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Specialized nanomaterials for screen printing, inkjet printing, and other advanced printing methodologies.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">Advanced Coatings</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Protective and functional coatings with antimicrobial, conductive, and optical properties.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Industries We Serve</h2>
            <p className="text-xl text-slate-600">
              Partnering with leading sectors to drive technological advancement
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              'Electronics',
              'Wearable Technology',
              'Advanced Coatings',
              'Display Manufacturing',
              'Flexible Electronics',
              'Li-ion Batteries',
              'Quantum Technologies',
              'Research & Development'
            ].map((industry, index) => (
              <Card key={index} className="border-2 border-slate-200 hover:border-blue-900 transition-colors text-center p-6 hover:shadow-lg">
                <CardContent className="p-0">
                  <p className="font-semibold text-slate-900">{industry}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality & Innovation Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Quality & Innovation</h2>
              <p className="text-xl text-slate-600">
                Committed to excellence through research, quality assurance, and continuous innovation
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <img 
                    src="https://images.unsplash.com/photo-1663365520163-cf0ef0afb744" 
                    alt="Quality Testing Equipment" 
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <CardTitle className="text-xl flex items-center gap-3">
                    <Shield className="w-7 h-7 text-blue-900" />
                    Quality Assurance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed">
                    Rigorous quality control processes ensure consistent product specifications, high purity levels, and reliable performance.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <img 
                    src="https://images.unsplash.com/photo-1720036236694-d0a231c52563" 
                    alt="Advanced Manufacturing" 
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <CardTitle className="text-xl flex items-center gap-3">
                    <Factory className="w-7 h-7 text-blue-900" />
                    Advanced Manufacturing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed">
                    State-of-the-art production facilities with high-throughput continuous processes for scalable nanomaterial manufacturing.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <img 
                    src="https://images.unsplash.com/photo-1569852573858-03c9e2877792" 
                    alt="Research & Development" 
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <CardTitle className="text-xl flex items-center gap-3">
                    <Microscope className="w-7 h-7 text-blue-900" />
                    Research & Development
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed">
                    Continuous innovation through research-driven approach, developing next-generation nanomaterial solutions.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Get In Touch</h2>
              <p className="text-xl text-blue-100">
                Ready to explore nanomaterial solutions for your business? Contact us today.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <Card className="border-none shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-900">Send us a message</CardTitle>
                  <CardDescription>We'll respond within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Doe"
                        className="border-slate-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="john@company.com"
                        className="border-slate-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">Company Name *</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        placeholder="Your Company"
                        className="border-slate-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="9876543210"
                        className="border-slate-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Tell us about your requirements..."
                        rows={4}
                        className="border-slate-300"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-blue-900 hover:bg-blue-800 text-lg py-6"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg mb-1">Address</p>
                        <p className="text-blue-100">
                          A 1123, Siddhi Vinayak Tower-A<br />
                          B/H DCP Office, Off S.G. Highway<br />
                          Makarba, Ahmedabad, Gujarat 380051
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg mb-1">Email</p>
                        <a href="mailto:info@nanorbital.com" className="text-blue-100 hover:text-white transition-colors">
                          info@nanorbital.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg mb-1">Phone</p>
                        <a href="tel:+919327908826" className="text-blue-100 hover:text-white transition-colors">
                          +91 93279 08826
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                  <h4 className="text-xl font-bold mb-4">Business Hours</h4>
                  <div className="space-y-2 text-blue-100">
                    <p>Monday - Friday: 9:00 AM - 6:00 PM IST</p>
                    <p>Saturday: 9:00 AM - 1:00 PM IST</p>
                    <p>Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2">
              <Logo className="mb-4" />
              <p className="text-slate-400 mb-4">
                Industry-leading manufacturer of high-purity silver nanowires and nanoparticles, delivering advanced material solutions for electronics, displays, and emerging technologies worldwide.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('products')} className="hover:text-white transition-colors">Products</button></li>
                <li><button onClick={() => scrollToSection('applications')} className="hover:text-white transition-colors">Applications</button></li>
                <li><button onClick={() => scrollToSection('industries')} className="hover:text-white transition-colors">Industries</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Contact</button></li>
                <li><span className="text-slate-500">Careers</span></li>
                <li><span className="text-slate-500">News</span></li>
                <li><span className="text-slate-500">Research</span></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-400 text-sm">
                © 2024 Nanorbital Advanced Materials LLP. All rights reserved.
              </p>
              <p className="text-slate-400 text-sm">
                Pioneering Nanomaterial Solutions
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
